import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import featureNames from '~/shared/constants/featureFlags';
import { ProductTag } from '~/shared/constants/products';
import { sizeAbbrToSizeStringMap } from '~/shared/constants/productSizes';
import { getBrandValue } from '~/shared/utils/brandNameHelper';
import { getDefaultProductFilters } from '~/shared/utils/getDefaultProductFilters';
import { getRecommendedWearItWithFilters } from '~/shared/utils/getRecommendedWearItWithFilters';
import useGender from '~/shared/utils/useGender';
import useGridGender from '~/shared/utils/useGridGender';
import useHasFeature from '~/shared/utils/useHasFeature';
import { getMembership } from '~/techstyle-shared/react-accounts';
import { useProductListing } from '~/techstyle-shared/react-products';
import { useSession } from '~/techstyle-shared/redux-core';

import useProfileSizes from './useProfileSizes';

export default function useWearItWith({
  product,
  productSource = 'wear_it_with',
}) {
  const { enabled: isRetailDiscountingEnabled } = useHasFeature(
    featureNames.RETAILPRICETEST
  );
  const productIds = product?.wearItWithProductIds ?? [];

  const hasProductIds = productIds.length > 0;
  const gridGender = useGridGender();

  const { profileSizes } = useProfileSizes();
  const profileSizesWithSizeString = {};

  if (profileSizes && typeof profileSizes === 'object') {
    for (const key in profileSizes) {
      const sizeCode = profileSizes[key];
      const sizeString = sizeAbbrToSizeStringMap[sizeCode];
      profileSizesWithSizeString[key] = sizeString;
    }
  }
  const { isLoggedIn } = useSession();
  const { genderFilterString } = { ...useGender() };

  const { enabled: removeOriginFlag } = useHasFeature(
    featureNames.REMOVE_ORIGIN
  );

  const shouldAutoFilterWearItWith =
    isLoggedIn && genderFilterString === gridGender;

  const baseParams = useMemo(
    () =>
      getRecommendedWearItWithFilters({
        masterProductIds: productIds,
        profileSizes: profileSizesWithSizeString,
        shouldAutoFilterWearItWith,
      }),
    [productIds, profileSizesWithSizeString, shouldAutoFilterWearItWith]
  );

  const productFilterOptions = useMemo(() => {
    return getBrandValue({
      default: {},
      savage: {
        autoExcludeClearanceFplIds: false,
      },
    });
  }, []);

  if (removeOriginFlag) {
    baseParams.excludeTagIds = [ProductTag.removeOrigin];
  }
  const initialFilters = useSelector((state) =>
    getDefaultProductFilters(
      getMembership(state),
      state.domain.tld,
      baseParams,
      productFilterOptions
    )
  );

  const options = {
    apiVersion: 2,
    productSource,
  };

  const { products = [] } = useProductListing(
    {
      ...initialFilters,
      retailDiscounting: isRetailDiscountingEnabled,
      gridGender,
    },
    options
  );

  const shouldDisplay = useMemo(
    () => hasProductIds && products.length > 0,
    [hasProductIds, products]
  );

  return {
    shouldDisplay,
    productIds,
    products,
    productSource,
  };
}
